import { useAuth } from "@clerk/clerk-react";
import { Box, DialogTitle } from "@mui/material";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { BrandsContext } from "contexts/Brands";
import {
  GamificationContext,
  LockedModalClickFrom,
} from "contexts/Gamification";
import {
  EmailBrandPage,
  OnboardingDialogsContext,
} from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachContactsContext } from "contexts/OutreachContacts";
import { OutreachSendContext } from "contexts/OutreachSend";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendContactsContext } from "contexts/QuickSendContacts";
import {
  QuickSendDrawerContext,
  QuickSendTabView,
  RecentContactDetails,
} from "contexts/QuickSendDrawer";
import {
  QuickSendEmailDraftsContext,
  QuickSendEmailDraftsProvider,
} from "contexts/QuickSendEmailDrafts";
import { QuickSendErrorContext } from "contexts/QuickSendError";
import { QuickSendOutreachTabsContext } from "contexts/QuickSendOutreachTabs";
import { QuickSendPaginationContext } from "contexts/QuickSendPagination";
import {
  QuickSendScheduleContext,
  QuickSendScheduleProvider,
} from "contexts/QuickSendSchedule";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { checkNoRecentContact } from "contexts/helpers/QuickSendHelpers";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BentoContact,
  NewBentoContact,
  PreviewMessage,
  SavedBrandStatus,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { Conditions } from "schemas/outreach";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";
import { getVariablesFromHtml, isInvalidEmail } from "utils/string";
import { getToday, isIsoStringPriorToCurrentTime } from "utils/time";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useCollection } from "utils/useCollection";
import { useDrafts } from "utils/useDrafts";
import { useGamification } from "utils/useGamification";
import { usePageSize } from "utils/usePageSize";
import { useRequests } from "utils/useRequests";
import { useSubscription } from "utils/useSubscription";
import useTemplate from "utils/useTemplate";

import { Template, TemplateContentType } from "../ContactList/schema";
import QuickSendDialogHeader from "./DialogHeader";
import BusinessHourDialog from "./Dialogs/BusinessHourDialog";
import RecentContactDialog from "./Dialogs/RecentContactDialog";
import EmptyPage from "./EmptyPage";
import PreviewPerContact from "./PreviewPerContact";
import SelectOutreach from "./PreviewPerContact/PreviewIndividual/SelectOutreach";
import SharedEmailParts from "./PreviewPerContact/PreviewIndividual/SharedEmailParts";
import QuickSendWrapper from "./Wrapper";
import styles from "./styles";

const QuickSendDrawer = () => {
  const { setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { updateTemplateWithProfileVariables } = useTemplate();
  const { tab } = useParams();
  const { getDrafts, restoreDraft } = useDrafts();
  const { isLargeRecommendedPage, isRecommendedPage, isOnboardingPage } =
    usePageSize();
  const navigate = useNavigate();
  const location = useLocation();

  const [recentContactDetails, setRecentContactDetails] =
    useState<RecentContactDetails | null>(null);

  // Contexts
  const drafts = getDrafts(tab);
  const {
    currentOrg,
    emailSettings,
    setEmailSettings,
    profile,
    gamificationProfile,
    totalEmailsSent,
  } = useContext(OrganizationUserContext);
  const {
    emailHealth,
    setEmailHealth,
    setOpenIntegrationDialog,
    fetchIntegrationHealth,
    userIntegration,
    setUserIntegration,
  } = useContext(UserIntegrationsContext);
  const { updateActionItems } = useContext(ActionItemsContext);

  // All QuickSend related contexts variables
  const {
    updatePaginationLocalState,
    getPaginationSource,
    getCollectionId: getPaginationCollectionId,
    isSavedBrandSource,
  } = useContext(QuickSendPaginationContext);
  const {
    bentoContact,
    bentoBrand,
    outreachMessages,
    handleFetchPreviews,
    setTemplateForQuickSend,
    templateForQuickSend,
    setOutreachMessages,
    clearDraftTemplates,
  } = useContext(QuickSendContext);
  const { updateLastContacted, bentoContacts } = useContext(
    QuickSendContactsContext,
  );
  const {
    emailBrandDialogOpen,
    setEmailBrandDialogPage,
    setCreateTemplateOnboardingDialogOpen,
  } = useContext(OnboardingDialogsContext);
  const { setOutreachTab } = useContext(QuickSendOutreachTabsContext);
  const { refreshDraftsList } = useDrafts();
  const { moveToUnsent: moveToUnsentInCollection } = useCollection();
  const { getCollectionBrands } = useContext(SavedBrandCollectionsContext);
  const { moveFromUnsentToSent, moveFromSentToUnsent } = useRequests();

  const {
    setView,
    view,
    closeDrawer,
    quickSendBrandId,
    sendFrom,
    isEdited,
    setIsEdited,
    referredEmail,
    setPreviewLoading,
    previewLoading,
    handleOpenQuickSendIndividual,
  } = useContext(QuickSendDrawerContext);

  const { setErrorsFromMessage } = useContext(QuickSendErrorContext);
  const { refreshGamificationProfile } = useGamification();

  const { getUserScheduledAt, isOutsideBusinessHour } = useContext(
    QuickSendScheduleContext,
  );
  const { createEmailDraft } = useContext(QuickSendEmailDraftsContext);
  const { setOpenLockedModal } = useContext(GamificationContext);

  const { setUpgradeDialogSource, subscription } =
    useContext(SubscriptionContext);
  const {
    decreaseEmailCount,
    increaseEmailCount,
    decreaseChatCount,
    increaseChatCount,
  } = useSubscription();
  const {
    brands,
    setBrands,
    similarBrands,
    setSimilarBrands,
    setSimilarBrandsCursor,
    removeBrandsFromList,
  } = useContext(BrandsContext);
  const { enqueueSend } = useContext(OutreachSendContext);
  const {
    fetchOutreachTemplates,
    fetchOutreachTemplatesLoading,
    defaultTemplates,
    setEditTemplateDialogId,
  } = useContext(OutreachTemplatesContext);
  const { handleFetchContacts, fetchContactsParams } = useContext(
    OutreachContactsContext,
  );
  // States
  const [sendLoading, setSendLoading] = useState<boolean>(false);
  const [updateOrgProfile, setUpdateOrgProfile] = useState<boolean>(false);

  const outreachDataRef = useRef();
  const editRef = useRef();

  const bottomRef = useRef<HTMLDivElement>();

  const handleTestEmail = async (): Promise<boolean> => {
    try {
      setSendLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/send-outreach-test`,
        "POST",
        {},
        {
          emails: outreachMessages,
          bentoBrandId: bentoBrand?.id,
          sendFrom,
        },
      );
      return true;
    } catch (error) {
      const needsToScroll = setErrorsFromMessage(
        outreachMessages,
        error?.message,
      );
      if (needsToScroll) {
        bottomRef?.current?.scroll();
      }
      if (error?.message?.includes("connect to Gmail")) {
        setEmailHealth(false);
        setOpenIntegrationDialog(true);
      } else {
        setAlert(
          error?.message ||
            "Something went wrong. Unable to send outreach email. Please reload and retry again.",
          "error",
        );
      }
      storeCurrentDraft();
      return false;
    } finally {
      setSendLoading(false);
    }
  };

  const handleSendEmail = async (
    sendAt: null | string = null,
    conditions?: Conditions,
    isTest: boolean = false,
  ): Promise<boolean> => {
    if (!bentoBrand?.id) return false;

    if (isTest) {
      return await handleTestEmail();
    }

    if (subscription && subscription?.chatMonthlyLimit <= 0) {
      setUpgradeDialogSource(UpgradeDialogSource.hits_limit);
      return false;
    }

    if (
      subscription?.remainingMessages != null &&
      subscription.remainingMessages <= 0
    ) {
      setUpgradeDialogSource(UpgradeDialogSource.hits_limit);
      return false;
    }

    if (!bentoContact) {
      setAlert("Please input an email address", "error");
      return false;
    }

    if (isInvalidEmail(bentoContact?.email || "")) {
      setAlert("Please input a valid email address", "error");
      return false;
    }

    if (bentoContact?.isArchived) {
      setAlert(
        "This contact is no longer available. Please refresh and try again.",
        "error",
      );
      return false;
    }

    if (!emailHealth) {
      setEmailHealth(false);
      setOpenIntegrationDialog(true);
      return false;
    }

    if (userIntegration?.outreachPaused) {
      setAlert(
        "You cannot send emails with this Gmail account at this time due to hitting a Gmail limit. Please contact hello@onbento.com for assistance.",
        "error",
      );
      // Refetch in the background so emailHealth/userIntegration are fresh by
      // the time the user dismisses the alert or tries again.
      if (currentOrg?.id) {
        fetchIntegrationHealth(currentOrg.id);
      }
      return false;
    }

    const hasSentBefore = (totalEmailsSent ?? 0) > 0;

    if (
      !currentOrg?.isPaidUser &&
      bentoContact?.sourcedFromUserInbox &&
      hasSentBefore
    ) {
      setUpgradeDialogSource(UpgradeDialogSource.exclusive_contact);
      return false;
    }

    if (
      gamificationProfile &&
      !gamificationProfile.unlockPremiumFeatures &&
      bentoContact?.isDirectContact
    ) {
      setOpenLockedModal(LockedModalClickFrom.email);
      return false;
    }

    const messageError = _validateOutreachMessages(outreachMessages);
    if (messageError) {
      setAlert(messageError, "error");
      return false;
    }

    if (sendAt && isIsoStringPriorToCurrentTime(sendAt)) {
      setAlert("You cannot schedule a time that has already passed", "error");
      return false;
    }

    if (
      (!conditions || !conditions.skipsRecentContact) &&
      !(await checkNoRecentContact(
        sendAt,
        bentoContact,
        updateLastContacted,
        setRecentContactDetails,
      ))
    ) {
      return false;
    }

    if (recentContactDetails) setRecentContactDetails(null);
    if (
      (!conditions || !conditions?.skipsBusinessHour) &&
      isOutsideBusinessHour(sendAt)
    ) {
      return false;
    }

    if (sendAt && isIsoStringPriorToCurrentTime(sendAt)) {
      setAlert("You cannot schedule a time that has already passed", "error");
      return false;
    }
    const scheduleAt = getUserScheduledAt(sendAt);

    const payload = {
      emails: outreachMessages,
      bentoBrandId: bentoBrand?.id,
      previewContact: bentoContact,
      scheduleAt,
      timezone: emailSettings?.timezone,
      hour: emailSettings?.hour,
      sendFrom,
    };

    sendOptimistically(payload, scheduleAt, !!sendAt);
    return true;
  };

  const sendOptimistically = (
    payload: Record<string, unknown>,
    scheduleAt: string | null | undefined,
    isScheduled: boolean,
  ) => {
    const capturedBrand = bentoBrand;
    const capturedBrandId = bentoBrand?.id;
    const capturedClickFrom = sendFrom;
    const capturedReferredEmail = referredEmail;
    const capturedTemplate = templateForQuickSend;
    const capturedProfile = profile;
    const capturedShouldUpdateOrgProfile = updateOrgProfile;
    const capturedContactEmail = bentoContact?.email || "";
    const capturedIsRecommendedPage = isRecommendedPage;

    // Snapshot brand position before removal so we can restore it on revert.
    const capturedBrandIndexInBrands = capturedBrand
      ? brands.findIndex((b) => Number(b.id) === Number(capturedBrand.id))
      : -1;
    const capturedBrandIndexInSimilarBrands = capturedBrand
      ? similarBrands.findIndex(
          (b) => Number(b.id) === Number(capturedBrand.id),
        )
      : -1;

    // Snapshot the saved-brand collection (if any) so undo can move the brand
    // back into its unsent bucket and re-attach its draft
    const capturedPaginationSource = capturedBrand
      ? getPaginationSource(capturedBrand)
      : undefined;
    const capturedIsSavedBrandSource = !!(
      capturedPaginationSource && isSavedBrandSource(capturedPaginationSource)
    );
    const capturedSavedCollectionId: number | null = capturedIsSavedBrandSource
      ? getPaginationCollectionId(capturedPaginationSource)
      : null;

    // Snapshot the brand's position in the collection's unsent list so undo
    // can put it back exactly where it was instead of prepending to the top.
    const capturedCollectionUnsentIndex =
      capturedBrand && capturedSavedCollectionId !== null
        ? (
            getCollectionBrands(
              capturedSavedCollectionId,
              SavedBrandStatus.unsent,
            ) ?? []
          ).findIndex(
            (sb) => Number(sb?.bentoBrandId) === Number(capturedBrand.id),
          )
        : -1;

    // Snapshot the autogenerated draft for this brand so undo can put it back
    // in the recommendations list at its original position.
    const draftsForTab = getDrafts(tab);
    const capturedDraftIndex = capturedBrand
      ? draftsForTab.findIndex(
          (d) => Number(d?.bentoBrandId) === Number(capturedBrand.id),
        )
      : -1;
    const capturedDraft =
      capturedDraftIndex > -1 ? draftsForTab[capturedDraftIndex] : null;

    setIsEdited(false);
    closeDrawer();
    applyOptimisticLocalStateFor(capturedBrand, scheduleAt);

    const revertOptimisticState = () => {
      if (capturedBrand && capturedBrandIndexInBrands > -1) {
        setBrands((prev) => {
          if (prev.some((b) => Number(b.id) === Number(capturedBrand.id))) {
            return prev;
          }
          const copy = makeDeepCopy(prev);
          copy.splice(capturedBrandIndexInBrands, 0, capturedBrand);
          return copy;
        });
      }
      if (capturedBrand && capturedBrandIndexInSimilarBrands > -1) {
        setSimilarBrands((prev) => {
          if (prev.some((b) => Number(b.id) === Number(capturedBrand.id))) {
            return prev;
          }
          const copy = makeDeepCopy(prev);
          copy.splice(capturedBrandIndexInSimilarBrands, 0, capturedBrand);
          return copy;
        });
        setSimilarBrandsCursor((prev) => prev + 1);
      }
      increaseEmailCount();
      if (capturedIsRecommendedPage) {
        increaseChatCount();
      }
      if (capturedBrand) {
        moveFromSentToUnsent(capturedBrand);
      }
      if (capturedBrand && capturedSavedCollectionId !== null) {
        moveToUnsentInCollection(
          capturedBrand,
          capturedSavedCollectionId,
          capturedCollectionUnsentIndex >= 0
            ? capturedCollectionUnsentIndex
            : undefined,
        );
      }
      if (capturedDraft) {
        restoreDraft(capturedDraft, capturedDraftIndex);
      }
    };

    enqueueSend({
      contactEmail: capturedContactEmail,
      payload,
      isScheduled,
      onSuccess: (res) => {
        if (res?.completedActionItemIds) {
          updateActionItems(res.completedActionItemIds);
        }
        if (res?.emailSetting) {
          setEmailSettings(res.emailSetting);
        }
        if (
          capturedShouldUpdateOrgProfile &&
          capturedTemplate?.length > 0 &&
          capturedProfile &&
          capturedTemplate[0]?.id
        ) {
          updateTemplateWithProfileVariables(
            capturedTemplate[0].id,
            capturedProfile,
            setTemplateForQuickSend,
          );
        }
      },
      onError: (err) => {
        // The send didn't happen — undo every optimistic mutation we made.
        revertOptimisticState();
        handleSendError(err);
      },
      onUndo: () => {
        revertOptimisticState();
        if (capturedBrandId) {
          handleOpenQuickSendIndividual(
            capturedBrandId,
            capturedClickFrom,
            capturedReferredEmail,
          );
        }
      },
    });
  };

  const handleSendError = (error: { message?: string } | Error) => {
    const message = (error as { message?: string })?.message;
    const needsToScroll = setErrorsFromMessage(outreachMessages, message);
    if (needsToScroll) {
      bottomRef?.current?.scroll();
    }
    if (message?.includes("connect to Gmail")) {
      setEmailHealth(false);
      setOpenIntegrationDialog(true);
    } else if (message?.includes("hitting a Gmail limit")) {
      setUserIntegration((prev) =>
        prev ? { ...prev, outreachPaused: true } : prev,
      );
      if (currentOrg?.id) {
        fetchIntegrationHealth(currentOrg.id);
      }
    } else if (
      message?.includes(
        "You must upgrade your account to email an exclusive contact.",
      )
    ) {
      setUpgradeDialogSource(UpgradeDialogSource.exclusive_contact);
    } else if (message?.includes("upgrade your account")) {
      setUpgradeDialogSource(UpgradeDialogSource.hits_limit);
    } else if (message?.includes("direct email address")) {
      setOpenLockedModal(LockedModalClickFrom.email);
    }
  };

  const applyOptimisticLocalStateFor = (
    brand: typeof bentoBrand,
    scheduleAt?: string | null,
  ) => {
    if (brand) removeBrandsFromList(brand);

    if (bentoContact?.id) {
      updateLastContacted([bentoContact?.id], true);
    }
    decreaseEmailCount();

    // update the page to 1, so that the data gets refetched
    const updatedParams = { ...fetchContactsParams };
    updatedParams.page = 1;
    handleFetchContacts(updatedParams);

    fetchOutreachTemplates();
    refreshGamificationProfile(false);

    // Onboarding Page, refresh state
    if (isOnboardingPage) {
      navigate(`/${routes.home}${location.search}`);
      return;
    }

    // First Email Dialog - move to next page
    if (emailBrandDialogOpen) {
      setEmailBrandDialogPage(EmailBrandPage.complete);
      return;
    }

    // Move to next BentoBrand
    if (brand) {
      const copy = makeDeepCopy(brand);
      if (scheduleAt) {
        copy.earliestPendingSendAt = scheduleAt;
        if (templateForQuickSend[0]?.id) {
          copy.latestOutreachTemplateId = templateForQuickSend[0]?.id;
        }
      } else {
        copy.latestEmailCompletedAt = getToday();
      }
      updatePaginationLocalState({
        bentoBrand: copy,
        handleClose,
      });
      moveFromUnsentToSent(copy);

      // Poll for autogenerated drafts if user sent from Recommendations page
      if (isRecommendedPage) {
        decreaseChatCount();
        refreshDraftsList(brand);
      }
    }
  };

  const handleClose = async () => {
    if (!isEdited) {
      outreachDataRef.current = undefined;
    }
    closeDrawer();
  };

  useEffect(() => {
    if (emailHealth) {
      setOpenIntegrationDialog(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailHealth]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    // @ts-ignore
    editRef.current = Boolean(isEdited);

    // Warn user if they try to close the brower but has a draft.
    const unloadCallback = (event: CustomEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    if (isEdited) {
      window.addEventListener("beforeunload", unloadCallback);
    }
    return () => {
      window.removeEventListener("beforeunload", unloadCallback);
    };
  }, [isEdited]);

  const storeCurrentDraft = () => {
    // @ts-ignore: Store the current draft to Ref so it doesn't
    // get destroyed when component unmount.
    outreachDataRef.current = {
      emails: outreachMessages,
      previewContact: bentoContact,
      bentoBrand,
      isAutogenerated: templateForQuickSend[0]?.isAutogenerated,
    };
  };

  useEffect(() => {
    storeCurrentDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachMessages, bentoBrand?.id, bentoContact]);

  useEffect(() => {
    return () => {
      const refValue = outreachDataRef?.current;
      const isEdited = editRef.current;
      if (refValue && isEdited) {
        // Create email draft if user exits this component
        createEmailDraft(refValue);
        setIsEdited(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickSendBrandId]);

  const fetchPreviewBrand = async (
    brandId: number,
    fetchDraft: boolean,
    updatedTemplates?: Template[],
    updatedDefaultTemplates?: Template[],
  ) => {
    setOutreachTab(0);
    setView(QuickSendTabView.EXPAND);
    let specificTemplates = defaultTemplates;
    if (updatedDefaultTemplates && updatedDefaultTemplates.length > 0) {
      specificTemplates = updatedDefaultTemplates;
    }
    return await handleFetchPreviews(
      brandId,
      {
        email: referredEmail,
        bentoBrandId: brandId,
      },
      specificTemplates,
      fetchDraft,
      updatedTemplates,
    );
  };

  const fetchInitialData = async () => {
    setPreviewLoading(Number(quickSendBrandId));
    setIsEdited(false);
    outreachDataRef.current = undefined;
    setTemplateForQuickSend([]);
    const clearedTemplates = clearDraftTemplates();

    let updatedTemplates;
    let updatedDefaultTemplates;
    if (clearedTemplates.length === 0) {
      // refretch templates in case a template was created in the background on onboarding, and is not in state yet
      // do not set the loading for outreach templates to avoid triggering a useEffect to fetch this data again
      [updatedTemplates, updatedDefaultTemplates] =
        await fetchOutreachTemplates(true);
    }

    const hasDraft = await fetchPreviewBrand(
      Number(quickSendBrandId),
      true,
      updatedTemplates,
      updatedDefaultTemplates,
    );

    const hasTemplate =
      (clearedTemplates && clearedTemplates.length > 0) ||
      (updatedTemplates && updatedTemplates.length > 0) ||
      hasDraft;

    if (!hasTemplate) {
      trackEvent("Quick Send Create Template Onboarding Dialog Opened");
      setCreateTemplateOnboardingDialogOpen(true);
    }

    if (
      hasTemplate &&
      !isOnboardingPage &&
      !emailBrandDialogOpen &&
      !isRecommendedPage
    ) {
      const firstOutreachTemplate =
        clearedTemplates.find(
          (t) => t.contentType === TemplateContentType.OUTREACH,
        ) ||
        updatedTemplates?.find(
          (t) => t.contentType === TemplateContentType.OUTREACH,
        );

      if (
        firstOutreachTemplate &&
        firstOutreachTemplate.id &&
        !profile?.reviewedFirstTemplate
      ) {
        trackEvent("Quick Send Review First Template Dialog Opened");
        setEditTemplateDialogId(firstOutreachTemplate.id.toString());
      }
    }
  };

  const initializedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (quickSendBrandId < 0) {
      initializedKeyRef.current = null;
      return;
    }
    const key = `${currentOrg?.id ?? ""}:${quickSendBrandId}`;
    if (!fetchOutreachTemplatesLoading && initializedKeyRef.current !== key) {
      initializedKeyRef.current = key;
      fetchInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickSendBrandId, fetchOutreachTemplatesLoading, currentOrg?.id]);

  // If no QuickSend is open, display an Empty page based on which view the user is on.
  if (
    quickSendBrandId < 0 ||
    view === QuickSendTabView.COLLAPSE ||
    (isLargeRecommendedPage &&
      (outreachMessages?.length === 0 || drafts.length === 0) &&
      previewLoading !== quickSendBrandId)
  ) {
    return <EmptyPage />;
  }

  const isLoading =
    previewLoading === quickSendBrandId || bentoBrand?.id !== quickSendBrandId;

  const changePreview = (
    name: string,
    text: string | undefined,
    index: number,
  ) => {
    setIsEdited(true);
    setOutreachMessages((prev: PreviewMessage[]) => {
      const copy = makeDeepCopy(prev);
      copy[index][name] = text;
      return copy;
    });
  };

  const handleAddNewContact = (
    newContact: NewBentoContact,
    handleClose: () => void,
  ) => {
    const invalid = isInvalidEmail(newContact?.email);
    if (invalid) {
      setAlert("Please input a valid email", "error");
      return;
    }
    const alreadyExist = bentoContacts?.find(
      (x: BentoContact | NewBentoContact) => x.email === newContact?.email,
    );
    if (alreadyExist) {
      setAlert(
        `Contact with email ${newContact?.email} already exists`,
        "error",
      );
      return;
    }
    if (bentoBrand?.id && newContact?.email) {
      handleFetchPreviews(bentoBrand?.id, newContact);
    }
    handleClose();
  };

  return (
    <>
      <QuickSendWrapper handleClose={handleClose}>
        <Box sx={styles.quickSendWrapper}>
          <Box sx={styles.titleWrapper}>
            <DialogTitle sx={styles.dialogTitleText}>
              {isLargeRecommendedPage ||
              isOnboardingPage ||
              emailBrandDialogOpen ? (
                <></>
              ) : (
                <QuickSendDialogHeader
                  handleClose={handleClose}
                  isLoading={isLoading}
                  bentoBrand={bentoBrand}
                />
              )}
              <SharedEmailParts
                changePreview={changePreview}
                handleAddNewContact={handleAddNewContact}
              />
              <SelectOutreach />
            </DialogTitle>
          </Box>
          <Box sx={styles.editorWrapper}>
            <Box sx={styles.dialogContent}>
              <PreviewPerContact
                handleSendEmail={handleSendEmail}
                sendLoading={sendLoading}
                changePreview={changePreview}
                setUpdateOrgProfile={setUpdateOrgProfile}
              />
              <Box ref={bottomRef} />
            </Box>
          </Box>
        </Box>
      </QuickSendWrapper>

      <BusinessHourDialog handleSendEmail={handleSendEmail} />

      <RecentContactDialog
        recentContactDetails={recentContactDetails}
        setRecentContactDetails={setRecentContactDetails}
        handleSendEmail={handleSendEmail}
      />
    </>
  );
};

export default function QuickSendIndividualDrawer() {
  return (
    <QuickSendScheduleProvider>
      <QuickSendEmailDraftsProvider>
        <QuickSendDrawer />
      </QuickSendEmailDraftsProvider>
    </QuickSendScheduleProvider>
  );
}

const _stripHtml = (html: string) => html.replace(/(<([^>]+)>)/gi, "").trim();

const _validateOutreachMessages = (
  messages: PreviewMessage[],
): string | null => {
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const isFollowup = i > 0;
    const label = isFollowup ? `follow-up ${i}` : "outreach email";

    if (!isFollowup && !msg?.subject?.trim()) {
      return `Please add a subject to your ${label} before sending.`;
    }
    if (!_stripHtml(msg?.body || "")) {
      return `Please write a body for your ${label} before sending.`;
    }
    const bodyTags = getVariablesFromHtml(msg?.body || "");
    // Follow-ups inherit the thread's subject, so a typed subject on a
    // follow-up never reaches the recipient — don't gate on its merge tags.
    const subjectTags =
      !isFollowup && msg?.subject
        ? (msg.subject.match(/\{[^}]+\}/g) || []).map((m) =>
            m.slice(1, -1).trim(),
          )
        : [];
    const unresolved = [...subjectTags, ...bodyTags];
    if (unresolved.length > 0) {
      return `Unresolved tag in ${label}: {${unresolved[0]}}. Please fill it in or remove it.`;
    }
  }
  return null;
};
